import { PatientResponse, PatientSearch } from '@quromedical/models';
import { omitby } from '@quromedical/utils';
import { useLinkTo } from '@react-navigation/native';
import { ColumnType, SortConfig, useTableState } from 'components/table';
import { getDisplayDate } from 'core/display';
import { DefinedGender, genderDisplay } from 'core/forms';
import { PaginatedTable } from 'core/table';
import { Section, Sidebar } from 'design-system/layout';
import { FetcherOptions, getField } from 'hooks/usePaginatedData';
import { PatientApi } from 'integration/aggregate';
import React, { useCallback, useState } from 'react';
import { strings } from 'strings';
import { trpc } from 'trpc';

import { Header } from './Header';

const api = new PatientApi();

const columns: ColumnType<PatientResponse>[] = [
  {
    accessor: 'general',
    id: 'givenName',
    header: strings.GivenName,
    disableSortBy: false,
    Cell: ({ value }) => value.givenName || '',
  } as ColumnType<PatientResponse, 'general'>,
  {
    accessor: 'general',
    id: 'familyName',
    header: strings.FamilyName,
    disableSortBy: false,
    Cell: ({ value }) => value.familyName || '',
  } as ColumnType<PatientResponse, 'general'>,
  {
    accessor: 'general',
    id: 'gender',
    header: strings.Gender,
    disableSortBy: false,
    Cell: ({ value }: { value: PatientResponse['general'] }) =>
      genderDisplay[value.gender as DefinedGender] || '',
  } as unknown as ColumnType<PatientResponse, 'general'>,
  {
    accessor: 'general',
    id: 'birthDate',
    header: strings.DateOfBirth,
    disableSortBy: false,
    Cell: ({ value }: { value: PatientResponse['general'] }) =>
      getDisplayDate(value?.birthDate) || '',
  } as unknown as ColumnType<PatientResponse, 'general'>,
];

type Filter = Pick<PatientSearch, 'name'>;

const all = '__all__';

const shouldOmitOption = (value?: string): boolean => {
  if (!value?.length) {
    return true;
  }

  return value === all;
};

const sanitizeFilter = (data: Filter) => omitby(data, shouldOmitOption);

const sortConfig: SortConfig<PatientResponse> = {
  'general.givenName': {
    asc: 'given',
    desc: '-given',
  },
  'general.familyName': {
    asc: 'family',
    desc: '-family',
  },
  'general.gender': {
    asc: 'gender',
    desc: '-gender',
  },
  'general.birthDate': {
    asc: 'birthdate',
    desc: '-birthdate',
  },
};

export const PatientListScreen: React.FC<{}> = () => {
  const sidebarProps = Sidebar.useResponsiveSidebarProps();
  const navigate = useLinkTo();
  const [filter, setFilter] = useState<Filter>({});

  const setSearch = useCallback((name?: string) => setFilter({ ...filter, name }), [filter]);

  const tableState = useTableState<PatientResponse>({
    sort: sortConfig,
  });

  const cacheKey = [api.getPatientSWRKey('list'), filter, tableState.sort];

  const fetcher = useCallback(
    (options?: FetcherOptions) => {
      const res = trpc.client.patient.getAll.query({
        sort: tableState.sort,
        pageToken: options?.nextToken,
        ...sanitizeFilter(filter),
        ...options,
      });
      return res;
    },
    [filter, tableState]
  );

  const onRowPress = useCallback(
    (row: PatientResponse) =>
      navigate({
        screen: 'Patient',
        params: {
          screen: 'View',
          params: {
            screen: 'Profile',
            id: row.id,
          },
        },
      }),
    [navigate]
  );

  return (
    <Sidebar.Container>
      <Sidebar.Main {...sidebarProps.main}>
        <Section>
          <Header onSearchChange={setSearch} screen="patients" />
        </Section>
        <Section hasBottomMargin>
          <PaginatedTable
            cacheKey={cacheKey}
            fetcher={fetcher}
            getDataFromResponse={getField('patients')}
            onRowPress={onRowPress}
            onTableStateChanged={tableState.onChange}
            columns={columns}
          />
        </Section>
      </Sidebar.Main>
    </Sidebar.Container>
  );
};
